// src/pages/ContactPage.js
// Lead-generation Contact page. Uses the shared <LeadForm /> which captures
// real leads into Firestore so they can be exported / mailed later.
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaBolt, FaShieldAlt, FaBell, FaHeadset, FaHandshake } from 'react-icons/fa';
import LeadForm from '../components/LeadForm';

/* ── design tokens (Wise-style, matches About/Home) ── */
const N  = '#0d2235';
const E  = '#29ABE2';
const Eo = '#1a8fc0';
const MT = '#5a7a8f';
const GREEN = '#16c784';

const pulse = keyframes`
  0%,100% { opacity: 1;   transform: scale(1);   }
  50%     { opacity: 0.6; transform: scale(0.85); }
`;

const fUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.09, duration: 0.5, ease: 'easeOut' },
  }),
};

const Wrap = styled.section`
  background: transparent !important;
  padding: 120px 10% 90px !important;
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) { padding: 96px 5% 64px !important; }
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  gap: 4.5rem;
  align-items: start;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    gap: 2.8rem;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 14px;
  border: 1.5px solid rgba(41,171,226,0.30);
  border-radius: 9999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${Eo};
  background: rgba(41,171,226,0.10);
  margin-bottom: 1.1rem;

  .dot { width: 7px; height: 7px; border-radius: 50%; background: ${GREEN}; animation: ${pulse} 1.7s ease-in-out infinite; }
`;

const H1 = styled.h1`
  font-size: clamp(2.3rem, 5vw, 3.8rem) !important;
  font-weight: 800;
  line-height: 1.0 !important;
  letter-spacing: -0.022em;
  color: ${N} !important;
  -webkit-text-fill-color: ${N} !important;
  margin: 0 0 1.1rem;

  em { font-style: normal; color: ${E} !important; -webkit-text-fill-color: ${E} !important; }
`;

const Lead = styled.p`
  font-size: 1.07rem;
  color: ${MT} !important;
  line-height: 1.72;
  max-width: 460px;
  margin: 0 0 2rem;
`;

const Perks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 2rem;
`;

const Perk = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;

  .ic {
    width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(41,171,226,0.12); color: ${E}; font-size: 1rem;
  }
  h4 { font-size: 0.96rem !important; font-weight: 700; color: ${N} !important; margin: 0 0 0.15rem; }
  p  { font-size: 0.86rem; color: ${MT} !important; line-height: 1.55; margin: 0; }
`;

const MailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  .mlabel {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${MT};
    margin-bottom: 0.15rem;
  }
`;

const DirectMail = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.92rem;
  font-weight: 600;
  color: ${Eo};
  text-decoration: none;

  svg { font-size: 1rem; flex-shrink: 0; }
  b { color: ${N}; font-weight: 700; }
  &:hover { text-decoration: underline; }
`;

const PERKS = [
  { ic: <FaBolt />,      t: 'AI market insights, weekly', d: 'Bitcoin & altcoin trends, signal breakdowns, and what the AI is watching — straight to your inbox.' },
  { ic: <FaBell />,      t: 'Early access to features', d: 'Be first to try new tools, alerts, and the TGEN token rollout before anyone else.' },
  { ic: <FaShieldAlt />, t: 'No spam, ever',            d: 'Unsubscribe in one click. We never sell or share your email. That is a promise.' },
];

const schema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Trend Genie',
  description: 'Get in touch with Trend Genie and subscribe for AI-powered crypto market insights.',
  url: 'https://www.trendgenie.io/contact',
};

function ContactPage() {
  return (
    <Wrap>
      <Helmet>
        <title>Contact Us | Trend Genie — AI Crypto Market Insights</title>
        <meta name="description" content="Get in touch with the Trend Genie team and subscribe to our newsletter for AI-powered crypto market insights, signal breakdowns, and early feature access. Free, no spam." />
        <link rel="canonical" href="https://www.trendgenie.io/contact" />
        <meta property="og:title" content="Contact Trend Genie | AI Crypto Market Insights" />
        <meta property="og:description" content="Subscribe for AI crypto market insights and reach the Trend Genie team. Free, no spam, unsubscribe anytime." />
        <meta property="og:url" content="https://www.trendgenie.io/contact" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <Inner>
        {/* LEFT — pitch */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fUp}>
          <Badge><span className="dot" /> Let's talk</Badge>
          <H1>Get crypto clarity,<br /><em>straight to your inbox.</em></H1>
          <Lead>
            Questions, partnerships, or just want smarter crypto market insights?
            Drop your email below — join the Trend Genie list and we'll keep you ahead of the market.
          </Lead>

          <Perks>
            {PERKS.map((p, i) => (
              <motion.div key={p.t} custom={i + 1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fUp}>
                <Perk>
                  <div className="ic">{p.ic}</div>
                  <div>
                    <h4>{p.t}</h4>
                    <p>{p.d}</p>
                  </div>
                </Perk>
              </motion.div>
            ))}
          </Perks>

          <MailBlock>
            <span className="mlabel">Prefer email?</span>
            <DirectMail href="mailto:support@trendgenie.io">
              <FaHeadset /> Support — <b>support@trendgenie.io</b>
            </DirectMail>
            <DirectMail href="mailto:contact@trendgenie.io">
              <FaHandshake /> Partnerships &amp; contact — <b>contact@trendgenie.io</b>
            </DirectMail>
          </MailBlock>
        </motion.div>

        {/* RIGHT — shared lead form */}
        <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, ease: 'easeOut' }}>
          <LeadForm source="contact_page" />
        </motion.div>
      </Inner>
    </Wrap>
  );
}

export default ContactPage;
